import axios from 'axios';
import { BASE_URL } from 'BaseURL';

export const AUTH_EXPIRED_EVENT = 'shiftware:auth-expired';

const DEFAULT_TIMEOUT_MS = 15000;
const LOCAL_DEV_HOST_ALIASES = {
    localhost: '127.0.0.1',
    '127.0.0.1': 'localhost',
};

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(String(url || ''));

export const resolveApiUrl = (url) => {
    if (isAbsoluteUrl(url)) return url;
    return `${BASE_URL}${String(url || '').replace(/^\/+/, '')}`;
};

export const getLocalDevFallbackUrl = (url) => {
    try {
        const parsedUrl = new URL(url);
        const fallbackHost = LOCAL_DEV_HOST_ALIASES[parsedUrl.hostname];
        if (!fallbackHost) return null;

        parsedUrl.hostname = fallbackHost;
        return parsedUrl.toString();
    } catch {
        return null;
    }
};

export const isRequestCanceled = (error) => Boolean(
    error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || error?.message === 'canceled'
    || (typeof axios.isCancel === 'function' && axios.isCancel(error))
);

export const buildAuthHeaders = (token) => (
    token ? { Authorization: `Bearer ${token}` } : {}
);

const withRequestDefaults = (config = {}) => ({
    ...config,
    timeout: config.timeout ?? DEFAULT_TIMEOUT_MS,
    headers: {
        Accept: 'application/json',
        ...(config.headers || {}),
    },
});

export const normalizeApiError = (error) => {
    const status = error?.response?.status || error?.status;
    const data = error?.response?.data || error?.data;
    const requestUrl = error?.requestUrl || error?.config?.url || error?.response?.config?.url;
    const method = error?.config?.method?.toUpperCase();
    const isNetworkError = !status && (
        error?.code === 'ERR_NETWORK'
        || error?.message === 'Network Error'
        || Boolean(error?.request)
    );
    const isTimeoutError = error?.code === 'ECONNABORTED';
    const fallbackMessage = isTimeoutError
        ? 'Request timed out while contacting the backend.'
        : 'Request failed.';
    const networkMessage = requestUrl
        ? `Network error while contacting ${requestUrl}. Check the backend URL and local CORS settings.`
        : 'Network error while contacting the backend. Check the backend URL and local CORS settings.';
    const message = isNetworkError
        ? networkMessage
        : data?.detail || data?.error || data?.message || error?.message || fallbackMessage;
    const apiError = new Error(message);
    apiError.status = status;
    apiError.data = data;
    apiError.isApiError = true;
    apiError.isNetworkError = isNetworkError;
    apiError.requestUrl = requestUrl;
    apiError.method = method;
    return apiError;
};

const handleApiError = (error) => {
    if (isRequestCanceled(error)) {
        throw error;
    }

    const normalizedError = normalizeApiError(error);
    if ([401, 403].includes(normalizedError.status) && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    throw normalizedError;
};

const shouldRetryWithLocalDevFallback = (error, requestUrl) => (
    !isRequestCanceled(error)
    && !error?.response
    && Boolean(getLocalDevFallbackUrl(requestUrl))
    && (
        error?.code === 'ERR_NETWORK'
        || error?.message === 'Network Error'
        || Boolean(error?.request)
    )
);

const requestWithFallback = async ({ method, url, data, config }) => {
    const requestUrl = resolveApiUrl(url);
    const requestConfig = withRequestDefaults(config);
    const runRequest = (nextUrl) => (
        method === 'post'
            ? axios.post(nextUrl, data, requestConfig)
            : axios.get(nextUrl, requestConfig)
    );

    try {
        return await runRequest(requestUrl);
    } catch (error) {
        if (isRequestCanceled(error)) {
            throw error;
        }

        if (shouldRetryWithLocalDevFallback(error, requestUrl)) {
            const fallbackUrl = getLocalDevFallbackUrl(requestUrl);
            try {
                return await runRequest(fallbackUrl);
            } catch (fallbackError) {
                if (isRequestCanceled(fallbackError)) {
                    throw fallbackError;
                }

                fallbackError.requestUrl = fallbackUrl;
                return handleApiError(fallbackError);
            }
        }

        error.requestUrl = requestUrl;
        return handleApiError(error);
    }
};

const apiClient = {
    get(url, config) {
        return requestWithFallback({ method: 'get', url, config });
    },

    post(url, data, config) {
        return requestWithFallback({ method: 'post', url, data, config });
    },
};

export default apiClient;
